const QualityAssuranceUnit1Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-teal-900 mb-3 border-b-2 border-slate-200 pb-2">Unit I: Quality Assurance & Quality Management Concepts</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            Quality is not an accident; it is the result of intelligent effort. This unit introduces the foundational concepts of maintaining and assuring quality in the pharmaceutical industry, including international guidelines (ICH, ISO) and systematic approaches like Total Quality Management (TQM) and Quality by Design (QbD).
        </p>
    </div>

    <!-- 2. QA, QC, and GMP -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-teal-800 mb-4 border-b border-slate-100 pb-2">1. Concepts of QA, QC, and GMP</h3>
        <p class="text-[13px] text-slate-700 mb-4">To understand quality management, it is crucial to differentiate between Quality Assurance (QA), Quality Control (QC), and Good Manufacturing Practices (GMP).</p>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div class="bg-teal-50 p-4 rounded-lg border border-teal-100 shadow-sm">
                <h4 class="font-bold text-teal-900 text-[15px] mb-2">Quality Assurance (QA)</h4>
                <p class="text-[12px] text-slate-600 mb-2"><strong>Process-Oriented (Proactive)</strong></p>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-1">
                    <li>A wide-ranging concept covering all matters that individually or collectively influence product quality.</li>
                    <li>Focuses on defect <em>prevention</em>.</li>
                    <li>Ensures that quality is "built into" the product during design and production.</li>
                </ul>
            </div>
            <div class="bg-cyan-50 p-4 rounded-lg border border-cyan-100 shadow-sm">
                <h4 class="font-bold text-cyan-900 text-[15px] mb-2">Quality Control (QC)</h4>
                <p class="text-[12px] text-slate-600 mb-2"><strong>Product-Oriented (Reactive)</strong></p>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-1">
                    <li>The part of GMP concerned with sampling, specifications, and testing.</li>
                    <li>Focuses on defect <em>identification</em> before release.</li>
                    <li>Ensures materials are not released for use/sale until their quality is judged satisfactory.</li>
                </ul>
            </div>
            <div class="bg-blue-50 p-4 rounded-lg border border-blue-100 shadow-sm">
                <h4 class="font-bold text-blue-900 text-[15px] mb-2">Good Manufacturing Practice (GMP)</h4>
                <p class="text-[12px] text-slate-600 mb-2"><strong>System-Oriented (Guidelines)</strong></p>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-1">
                    <li>The part of QA which ensures that products are consistently produced and controlled to quality standards.</li>
                    <li>Minimizes risks involved in pharmaceutical production (e.g., cross-contamination, mix-ups) that cannot be eliminated through final testing alone.</li>
                </ul>
            </div>
        </div>
    </div>

    <!-- 3. Total Quality Management (TQM) -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-teal-800 mb-3 border-b border-slate-100 pb-2">2. Total Quality Management (TQM)</h3>
        <p class="text-[13px] text-slate-700 mb-4 text-justify">
            TQM is a comprehensive and structured approach to organizational management that seeks to improve the quality of products and services through ongoing refinements in response to continuous feedback.
        </p>

        <div class="bg-slate-50 p-4 rounded-lg border border-slate-200 text-[13px] text-slate-700">
            <h4 class="font-bold text-teal-900 mb-2">Core Elements & Philosophies of TQM:</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                <ul class="list-disc pl-5 space-y-1">
                    <li><strong>Customer-Focused:</strong> The customer ultimately determines the level of quality.</li>
                    <li><strong>Total Employee Involvement:</strong> All employees participate in working toward common goals.</li>
                    <li><strong>Process-Centered:</strong> A fundamental part of TQM is a focus on process thinking.</li>
                    <li><strong>Integrated System:</strong> Everyone understands the vision, mission, and guiding principles.</li>
                </ul>
                <ul class="list-disc pl-5 space-y-1">
                    <li><strong>Strategic & Systematic Approach:</strong> Quality is a core component of the strategic plan.</li>
                    <li><strong>Continual Improvement:</strong> Drives the organization to be both analytical and creative.</li>
                    <li><strong>Fact-Based Decision Making:</strong> Requires performance tracking data.</li>
                    <li><strong>Communications:</strong> Effective communication maintains morale and motivates employees.</li>
                </ul>
            </div>
        </div>
    </div>

    <!-- 4. ICH Guidelines -->
    <div class="bg-blue-50 p-6 border border-blue-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-blue-900 mb-3 border-b border-blue-200 pb-2">3. ICH Guidelines</h3>
        <p class="text-[13px] text-slate-700 mb-3">
            The <strong>International Council for Harmonisation (ICH)</strong> brings together regulatory authorities and the pharmaceutical industry to discuss scientific and technical aspects of drug registration.
            <br><em>Purpose:</em> To achieve greater harmonization worldwide, ensuring safe, effective, and high-quality medicines are developed efficiently.
        </p>

        <!-- QSEM Overview -->
        <h4 class="font-bold text-blue-800 text-[15px] mb-2">Brief Overview of QSEM:</h4>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-5 text-[12px] text-slate-700">
            <div class="bg-white p-3 rounded border border-blue-100 shadow-sm">
                <p class="font-black text-blue-900 text-lg">Q</p>
                <p class="font-bold">Quality Guidelines</p>
                <p class="mt-1 text-slate-500">Stability, impurities, GMP, and quality risk management.</p>
            </div>
            <div class="bg-white p-3 rounded border border-blue-100 shadow-sm">
                <p class="font-black text-green-700 text-lg">S</p>
                <p class="font-bold">Safety Guidelines</p>
                <p class="mt-1 text-slate-500">In vitro and in vivo pre-clinical testing (carcinogenicity, genotoxicity).</p>
            </div>
            <div class="bg-white p-3 rounded border border-blue-100 shadow-sm">
                <p class="font-black text-red-700 text-lg">E</p>
                <p class="font-bold">Efficacy Guidelines</p>
                <p class="mt-1 text-slate-500">Clinical studies, dose-response, and Good Clinical Practice (GCP).</p>
            </div>
            <div class="bg-white p-3 rounded border border-blue-100 shadow-sm">
                <p class="font-black text-purple-700 text-lg">M</p>
                <p class="font-bold">Multidisciplinary</p>
                <p class="mt-1 text-slate-500">Cross-cutting topics like MedDRA, CTD (Common Technical Document).</p>
            </div>
        </div>

        <!-- Emphasis on Q Series -->
        <div class="bg-white p-4 rounded-lg border border-blue-200">
            <h4 class="font-bold text-blue-900 text-[14px] mb-2">Special Emphasis on Q-Series (Quality) & Stability</h4>
            <ul class="list-disc pl-5 space-y-1 text-[12px] text-slate-600">
                <li><strong>Q1 Series (Stability Testing):</strong> Defines stability data package required for new drugs in various climatic zones. Assesses how the quality of a drug substance varies with time under the influence of temperature, humidity, and light.</li>
                <li><strong>Q8 (Pharmaceutical Development):</strong> Discusses QbD.</li>
                <li><strong>Q9 (Quality Risk Management):</strong> Principles and tools for risk management.</li>
                <li><strong>Q10 (Pharmaceutical Quality System):</strong> Model for an effective quality management system based on ISO concepts.</li>
            </ul>
        </div>
    </div>

    <!-- 5. Quality by Design (QbD) -->
    <div class="bg-white p-6 mt-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-teal-800 mb-3 border-b border-slate-100 pb-2">4. Quality by Design (QbD)</h3>
        <p class="text-[13px] text-slate-700 mb-4">
            <strong>Definition:</strong> A systematic approach to development that begins with predefined objectives and emphasizes product and process understanding and process control, based on sound science and quality risk management (ICH Q8). <br>
            <em>"Quality should be built into the product by design, not tested into the product."</em>
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-teal-50 p-4 border border-teal-100 rounded-lg">
                <h4 class="font-bold text-teal-900 text-[14px] mb-2">Key Elements of QbD</h4>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-2">
                    <li><strong>QTPP (Quality Target Product Profile):</strong> Defines the desired quality profile of the drug (e.g., dosage form, route, strength).</li>
                    <li><strong>CQA (Critical Quality Attributes):</strong> Physical, chemical, biological, or microbiological properties that must be within an appropriate limit to ensure desired product quality.</li>
                    <li><strong>CMA & CPP:</strong> Identifying Critical Material Attributes and Critical Process Parameters that impact CQAs.</li>
                    <li><strong>Design Space:</strong> The multidimensional combination and interaction of input variables and process parameters that provide assurance of quality.</li>
                </ul>
            </div>
            <div class="bg-teal-50 p-4 border border-teal-100 rounded-lg">
                <h4 class="font-bold text-teal-900 text-[14px] mb-2">QbD Tools</h4>
                <ul class="list-disc pl-4 text-[12px] text-slate-600 space-y-2">
                    <li><strong>Design of Experiments (DoE):</strong> A structured method to determine the relationship between factors affecting a process and the output of that process.</li>
                    <li><strong>Quality Risk Management (QRM):</strong> Tools like Ishikawa (Fishbone) diagrams and FMEA (Failure Mode and Effects Analysis) to identify and mitigate risks.</li>
                    <li><strong>PAT (Process Analytical Technology):</strong> Real-time monitoring of critical process parameters.</li>
                </ul>
            </div>
        </div>
    </div>

    <!-- 6. ISO & NABL -->
    <div class="bg-white p-6 mt-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-teal-800 mb-4 border-b border-slate-100 pb-2">5. ISO 9000, ISO 14000 & NABL Accreditation</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            <div>
                <h4 class="text-[15px] font-bold text-teal-900 mb-2">A. ISO 9000 & ISO 14000 Series</h4>
                <ul class="list-disc pl-5 space-y-2 text-[12px] text-slate-700">
                    <li><strong>ISO 9000:</strong> Focuses on <em>Quality Management Systems (QMS)</em>. Helps organizations ensure they meet customer and regulatory requirements related to a product or service.</li>
                    <li><strong>ISO 14000:</strong> Focuses on <em>Environmental Management Systems (EMS)</em>. Helps organizations minimize their negative impact on the environment and comply with laws.</li>
                    <li><strong>Benefits:</strong> Global recognition, improved operational efficiency, reduced waste, enhanced customer satisfaction.</li>
                    <li><strong>Steps for Registration:</strong> Application &rarr; Document Review &rarr; Pre-assessment &rarr; Certification Audit &rarr; Issuance of Certificate &rarr; Surveillance Audits.</li>
                </ul>
            </div>
            
            <div>
                <h4 class="text-[15px] font-bold text-teal-900 mb-2">B. NABL Accreditation</h4>
                <p class="text-[12px] text-slate-700 mb-2"><strong>National Accreditation Board for Testing and Calibration Laboratories (NABL)</strong> provides formal recognition of technical competence of a testing or calibration lab.</p>
                <ul class="list-disc pl-5 space-y-2 text-[12px] text-slate-700">
                    <li><strong>Principles:</strong> Based on the international standard <strong>ISO/IEC 17025</strong>. It ensures the lab generates valid, reliable, and unbiased test results.</li>
                    <li><strong>Procedure:</strong> Preparation of quality manual &rarr; Application to NABL &rarr; Pre-assessment by lead assessor &rarr; Final assessment by expert team &rarr; Corrective actions by lab &rarr; Grant of Accreditation.</li>
                </ul>
            </div>
        </div>
    </div>

</div>
`;

export default QualityAssuranceUnit1Notes;